import React, { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
// Utility for class names if it doesn't exist, I'll define it inline or create a utils file.
// Since I can't be sure utils exists yet, I'll create a local helper or assume standard structure.
// I'll create a utils file in the next batch to be safe.
const buttonVariants = cva('inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50', {
  variants: {
    variant: {
      default: 'bg-[#25D366] text-black hover:bg-[#128C7E] hover:text-white font-bold shadow-[0_0_15px_rgba(37,211,102,0.5)] hover:shadow-[0_0_25px_rgba(37,211,102,0.7)] transition-all duration-300',
      destructive: 'bg-red-500 text-destructive-foreground hover:bg-red-500/90',
      outline: 'border border-[#25D366] bg-transparent text-[#25D366] hover:bg-[#25D366] hover:text-black',
      secondary: 'bg-slate-800 text-white hover:bg-slate-700',
      ghost: 'hover:bg-accent hover:text-accent-foreground',
      link: 'text-primary underline-offset-4 hover:underline'
    },
    size: {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 rounded-md px-3',
      lg: 'h-14 rounded-md px-8 text-lg',
      icon: 'h-10 w-10'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} {...props} data-id="element-182" />;
});
Button.displayName = 'Button';
export { Button, buttonVariants };